import { useState, useEffect } from 'react';
import {
  Key,
  Plus,
  Trash2,
  Copy,
  RotateCcw,
  Eye,
  EyeOff,
  Check,
  X,
  AlertTriangle,
  Clock,
  Activity,
  Shield,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  Package,
  Pencil,
  BookOpen,
} from 'lucide-react';
import { Button, Card, Spinner, Badge, Modal, useToast } from '@centraoneplus/ui';
import apiKeysService, {
  ApiKey,
  ApiKeyWithSecret,
  ApiKeyPermission,
  CreateApiKeyDto,
  ApiKeyUsage,
  ApiKeyUsageSummary,
  PRODUCT_NAME_MAX_LENGTH,
  IP_ALLOW_LIST_MAX_ENTRIES,
  parseIpAllowList,
} from '@services/api-keys.service';
import ApiKeyUsageBreakdown from './ApiKeyUsageBreakdown';
import ApiKeyRequestLog from './ApiKeyRequestLog';
import { useAuthStore } from '@stores/authStore';
import { useEntitlements } from '@features/plan/hooks';
import { Link, useLocation } from 'react-router-dom';
import IntegrationGuide from './integration-guide/IntegrationGuide';

// Product label input, shared by the create and edit modals. A native
// <datalist> offers the product names already used on this tenant's keys so
// keys for the same product share one spelling (the name becomes the ticket
// source, so "Control Tower" vs "ControlTower" would split it in two).
function ProductNameField({
  value,
  onChange,
  suggestions,
  inputId,
}: {
  value: string;
  onChange: (value: string) => void;
  suggestions: string[];
  inputId: string;
}) {
  const listId = `${inputId}-suggestions`;
  return (
    <div>
      <label
        htmlFor={inputId}
        className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
      >
        Product
      </label>
      <input
        id={inputId}
        type="text"
        list={suggestions.length > 0 ? listId : undefined}
        value={value}
        maxLength={PRODUCT_NAME_MAX_LENGTH}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        placeholder="e.g., Control Tower (optional)"
        autoComplete="off"
      />
      {suggestions.length > 0 && (
        <datalist id={listId}>
          {suggestions.map((name) => (
            <option key={name} value={name} />
          ))}
        </datalist>
      )}
      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
        Tickets created with this key are labelled with this product. Keys for the same
        product should use the same name.
      </p>
    </div>
  );
}

// Source-IP allow-list input, shared by the create and edit modals. One entry
// per line; the api-gateway refuses the key from any other address.
function IpAllowListField({
  value,
  onChange,
  inputId,
}: {
  value: string;
  onChange: (value: string) => void;
  inputId: string;
}) {
  const { entries, invalid } = parseIpAllowList(value);
  const tooMany = entries.length > IP_ALLOW_LIST_MAX_ENTRIES;
  return (
    <div>
      <label
        htmlFor={inputId}
        className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
      >
        Allowed IP addresses
      </label>
      <textarea
        id={inputId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        spellCheck={false}
        className="w-full px-3 py-2 font-mono border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        placeholder={'203.0.113.5\n198.51.100.0/24'}
        aria-invalid={invalid.length > 0 || tooMany}
        aria-describedby={`${inputId}-help`}
      />
      <p id={`${inputId}-help`} className="mt-1 text-xs text-gray-500 dark:text-gray-400">
        One IPv4/IPv6 address or CIDR range per line. Leave empty to accept the key from any
        address. Requests from anywhere else are refused with <code>ip_not_allowed</code>.
      </p>
      {invalid.length > 0 && (
        <p className="mt-1 text-xs text-red-600 dark:text-red-400">
          Not an IP address or CIDR range: {invalid.join(', ')}
        </p>
      )}
      {tooMany && (
        <p className="mt-1 text-xs text-red-600 dark:text-red-400">
          At most {IP_ALLOW_LIST_MAX_ENTRIES} entries; use a CIDR range to cover a network.
        </p>
      )}
    </div>
  );
}

/** True when the allow-list text can be saved as-is. */
function isIpAllowListValid(text: string): boolean {
  const { entries, invalid } = parseIpAllowList(text);
  return invalid.length === 0 && entries.length <= IP_ALLOW_LIST_MAX_ENTRIES;
}

// Change which source IPs an existing key is accepted from.
function EditIpAllowListModal({
  apiKey,
  onClose,
  onSaved,
}: {
  apiKey: ApiKey | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setValue((apiKey?.ipWhitelist ?? []).join('\n'));
  }, [apiKey]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!apiKey || !isIpAllowListValid(value)) return;
    setSaving(true);
    try {
      // An empty list clears the restriction.
      await apiKeysService.updateApiKey(apiKey.id, {
        ipWhitelist: parseIpAllowList(value).entries,
      });
      toast.success('Allowed IP addresses updated');
      onSaved();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to update allowed IP addresses');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={!!apiKey}
      onClose={onClose}
      title={`Allowed IP addresses for “${apiKey?.name ?? ''}”`}
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <IpAllowListField inputId="edit-api-key-ips" value={value} onChange={setValue} />
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Takes effect on the next request. Check the key's recent requests to see which address
          your integration calls from.
        </p>
        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={saving || !isIpAllowListValid(value)}>
            {saving ? <Spinner size="sm" className="mr-2" /> : null}
            Save
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// Edit the product label of an existing key. Product and allowed IPs can change
// after creation; the rest of a key's settings are fixed (rotate or recreate).
function EditProductModal({
  apiKey,
  suggestions,
  onClose,
  onSaved,
}: {
  apiKey: ApiKey | null;
  suggestions: string[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setValue(apiKey?.productName ?? '');
  }, [apiKey]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!apiKey) return;
    setSaving(true);
    try {
      await apiKeysService.updateApiKey(apiKey.id, {
        // Blank clears the product.
        productName: apiKeysService.normalizeProductName(value) ?? null,
      });
      toast.success('Product updated');
      onSaved();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to update product');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={!!apiKey} onClose={onClose} title={`Product for “${apiKey?.name ?? ''}”`} size="md">
      <form onSubmit={handleSubmit} className="space-y-4">
        <ProductNameField
          inputId="edit-api-key-product"
          value={value}
          onChange={setValue}
          suggestions={suggestions}
        />
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Changing the product affects tickets created from now on; existing tickets keep the
          source they were created with.
        </p>
        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={saving}>
            {saving ? <Spinner size="sm" className="mr-2" /> : null}
            Save
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// Key Creation Modal
function CreateKeyModal({
  isOpen,
  onClose,
  onCreated,
  productSuggestions,
}: {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (key: ApiKeyWithSecret) => void;
  productSuggestions: string[];
}) {
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<CreateApiKeyDto>({
    name: '',
    description: '',
    permissions: [],
    rateLimit: 1000,
  });
  const [expiryType, setExpiryType] = useState<'never' | '30' | '90' | '365' | 'custom'>('never');
  const [customExpiry, setCustomExpiry] = useState('');
  const [ipAllowList, setIpAllowList] = useState('');

  const groupedPermissions = apiKeysService.getGroupedPermissions();

  const handlePermissionToggle = (permission: ApiKeyPermission) => {
    setFormData((prev) => ({
      ...prev,
      permissions: prev.permissions.includes(permission)
        ? prev.permissions.filter((p) => p !== permission)
        : [...prev.permissions, permission],
    }));
  };

  const handleSelectAllCategory = (category: string, permissions: ApiKeyPermission[]) => {
    const allSelected = permissions.every((p) => formData.permissions.includes(p));
    if (allSelected) {
      setFormData((prev) => ({
        ...prev,
        permissions: prev.permissions.filter((p) => !permissions.includes(p)),
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        permissions: [...new Set([...prev.permissions, ...permissions])],
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Please enter a name for the API key');
      return;
    }
    if (formData.permissions.length === 0) {
      toast.error('Please select at least one permission');
      return;
    }
    if (!isIpAllowListValid(ipAllowList)) {
      toast.error('Fix the allowed IP addresses first');
      return;
    }
    const ipWhitelist = parseIpAllowList(ipAllowList).entries;

    let expiresAt: string | undefined;
    if (expiryType !== 'never') {
      const days = expiryType === 'custom' ? parseInt(customExpiry, 10) : parseInt(expiryType, 10);
      if (days && days > 0) {
        const expiry = new Date();
        expiry.setDate(expiry.getDate() + days);
        expiresAt = expiry.toISOString();
      }
    }

    setLoading(true);
    try {
      const newKey = await apiKeysService.createApiKey({
        ...formData,
        // Blank means "no product"; the API rejects an empty string.
        productName: apiKeysService.normalizeProductName(formData.productName),
        expiresAt,
        ...(ipWhitelist.length > 0 ? { ipWhitelist } : {}),
      });
      onCreated(newKey);
      toast.success('API key created successfully');
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to create API key');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Create API Key" size="lg">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Info */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Key Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="e.g., Production Integration"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Description
            </label>
            <textarea
              value={formData.description || ''}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              rows={2}
              placeholder="What is this API key used for?"
            />
          </div>
          <ProductNameField
            inputId="create-api-key-product"
            value={formData.productName || ''}
            onChange={(productName) => setFormData({ ...formData, productName })}
            suggestions={productSuggestions}
          />
        </div>

        {/* Permissions */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Permissions *
          </label>
          <div className="space-y-4 max-h-64 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            {Object.entries(groupedPermissions).map(([category, permissions]) => {
              const allSelected = permissions.every((p) => formData.permissions.includes(p));
              const someSelected = permissions.some((p) => formData.permissions.includes(p));
              return (
                <div key={category}>
                  <div className="flex items-center gap-2 mb-2">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      ref={(input) => {
                        if (input) input.indeterminate = someSelected && !allSelected;
                      }}
                      onChange={() => handleSelectAllCategory(category, permissions)}
                      className="rounded text-primary-600"
                    />
                    <span className="text-sm font-medium text-gray-900 dark:text-white">{category}</span>
                  </div>
                  <div className="ml-6 space-y-2">
                    {permissions.map((permission) => (
                      <label key={permission} className="flex items-start gap-2">
                        <input
                          type="checkbox"
                          checked={formData.permissions.includes(permission)}
                          onChange={() => handlePermissionToggle(permission)}
                          className="mt-0.5 rounded text-primary-600"
                        />
                        <div>
                          <span className="text-sm text-gray-700 dark:text-gray-300">
                            {apiKeysService.getPermissionLabel(permission)}
                          </span>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {apiKeysService.getPermissionDescription(permission)}
                          </p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Expiry */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Expiration
          </label>
          <div className="flex gap-2 flex-wrap">
            {[
              { value: 'never', label: 'Never' },
              { value: '30', label: '30 days' },
              { value: '90', label: '90 days' },
              { value: '365', label: '1 year' },
              { value: 'custom', label: 'Custom' },
            ].map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => setExpiryType(option.value as typeof expiryType)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  expiryType === option.value
                    ? 'bg-primary-100 text-primary-700 border-2 border-primary-500'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 border-2 border-transparent hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
          {expiryType === 'custom' && (
            <div className="mt-2 flex items-center gap-2">
              <input
                type="number"
                value={customExpiry}
                onChange={(e) => setCustomExpiry(e.target.value)}
                className="w-24 px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-sm"
                placeholder="Days"
                min={1}
              />
              <span className="text-sm text-gray-600 dark:text-gray-400">days from now</span>
            </div>
          )}
        </div>

        {/* Rate Limit */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Rate Limit (requests/minute)
          </label>
          <input
            type="number"
            value={formData.rateLimit || 1000}
            onChange={(e) => setFormData({ ...formData, rateLimit: parseInt(e.target.value, 10) })}
            className="w-32 px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            min={1}
          />
        </div>

        {/* Source IP allow-list */}
        <IpAllowListField
          inputId="create-api-key-ips"
          value={ipAllowList}
          onChange={setIpAllowList}
        />

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={loading}>
            {loading ? <Spinner size="sm" className="mr-2" /> : <Key className="h-4 w-4 mr-2" />}
            Create API Key
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// New Key Display Modal
function NewKeyModal({
  isOpen,
  onClose,
  apiKey,
  guidePath,
}: {
  isOpen: boolean;
  onClose: () => void;
  apiKey: ApiKeyWithSecret | null;
  guidePath: string;
}) {
  const toast = useToast();
  const [showKey, setShowKey] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!apiKey) return;
    try {
      await navigator.clipboard.writeText(apiKey.key);
      setCopied(true);
      toast.success('API key copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy');
    }
  };

  if (!apiKey) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="API Key Created" size="md">
      <div className="space-y-4">
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex gap-3">
            <AlertTriangle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-sm font-medium text-yellow-800">
                Save your API key now
              </h4>
              <p className="text-sm text-yellow-700 mt-1">
                This is the only time you'll see the full key. Store it securely - you won't be able to view it again.
              </p>
            </div>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Your API Key
          </label>
          <div className="flex items-center gap-2">
            <div className="flex-1 p-3 bg-gray-100 dark:bg-gray-700 rounded-lg font-mono text-sm break-all">
              {showKey ? apiKey.key : '•'.repeat(64)}
            </div>
            <button
              onClick={() => setShowKey(!showKey)}
              className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
              title={showKey ? 'Hide' : 'Show'}
            >
              {showKey ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
            <button
              onClick={handleCopy}
              className={`p-2 ${copied ? 'text-green-600' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'}`}
              title="Copy"
            >
              {copied ? <Check className="h-5 w-5" /> : <Copy className="h-5 w-5" />}
            </button>
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 pt-4 border-t">
          <Link
            to={guidePath}
            onClick={onClose}
            className="inline-flex items-center gap-1.5 text-sm text-primary-600 dark:text-primary-400 hover:underline"
          >
            <BookOpen className="h-4 w-4" />
            Next: integration guide
          </Link>
          <Button onClick={onClose}>
            I've saved my key
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// Usage Chart Component
function UsageChart({ usage }: { usage: ApiKeyUsage[] }) {
  // Empty series — tenant-service returned nothing (usage tracking still
  // settling in for this key) OR the BFF degraded to [] on an upstream
  // failure. Either way, render an honest empty state, not a blank box.
  if (!usage.length) {
    return (
      <div className="h-32 flex items-center justify-center text-sm text-gray-400 dark:text-gray-500">
        No usage data available yet
      </div>
    );
  }

  const maxRequests = Math.max(...usage.map((u) => u.requests));

  // All days have zero traffic — common for newly-issued keys, and for
  // keys whose owner uses JWT auth elsewhere and only intermittently
  // exercises the API-key path. Show the date row but suppress the
  // empty 4px bars that would look like noise.
  if (maxRequests === 0) {
    return (
      <div className="h-32 flex flex-col items-center justify-center gap-2">
        <div className="text-sm text-gray-400 dark:text-gray-500">
          No requests in the last {usage.length} {usage.length === 1 ? 'day' : 'days'}
        </div>
        <div className="text-xs text-gray-400 dark:text-gray-500">
          Requests authenticated with this key will appear here within a few minutes.
        </div>
      </div>
    );
  }

  return (
    <div className="h-32 flex items-end gap-1">
      {usage.map((day, index) => {
        const height = maxRequests > 0 ? (day.requests / maxRequests) * 100 : 0;
        const errorRate = day.requests > 0 ? (day.errors / day.requests) * 100 : 0;
        return (
          <div key={index} className="flex-1 flex flex-col items-center gap-1">
            <div className="text-xs text-gray-500 dark:text-gray-400">{day.requests}</div>
            <div
              className="w-full rounded-t transition-all"
              style={{ height: `${height}%`, minHeight: '4px' }}
            >
              <div
                className={`h-full rounded-t ${errorRate > 10 ? 'bg-red-500' : 'bg-primary-500'}`}
              />
            </div>
            <div className="text-xs text-gray-400 dark:text-gray-500">
              {new Date(day.date).toLocaleDateString('en-US', { weekday: 'short' })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Single stat tile used in the expanded-card stats strip. `accent='red'`
// switches the value to the error palette — applied when the metric
// represents a problem (errors > 0, error rate > 10%) so a noisy key
// surfaces at a glance.
function Stat({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent?: 'red';
}) {
  const valueColor =
    accent === 'red'
      ? 'text-red-600 dark:text-red-400'
      : 'text-gray-900 dark:text-white';
  return (
    <div className="rounded-lg bg-gray-50 dark:bg-gray-800/40 px-3 py-2">
      <div className="text-[11px] uppercase tracking-wider text-gray-500 dark:text-gray-400">
        {label}
      </div>
      <div className={`mt-0.5 text-lg font-semibold tabular-nums ${valueColor}`}>
        {value}
      </div>
    </div>
  );
}

// Main Component
export default function ApiKeyManagement() {
  const toast = useToast();
  const { user } = useAuthStore();
  const entitlements = useEntitlements(user?.tenantId);
  // P0.2 UX — disable creation when the tenant's plan doesn't include
  // `security.api-keys`. The backend (tenant-service ApiKeysService.create
  // + api-gateway ApiKeyAuthGuard) enforces the same gate; this layer is
  // purely so users see the upgrade path instead of a 402 after clicking.
  // Default to `true` while the snapshot is loading so admins on Growth/
  // Professional don't see the button flicker disabled on first paint.
  const apiKeysEntitled =
    !entitlements.data ||
    (entitlements.data.features ?? []).includes('security.api-keys');
  const requiredPlanForApiKeys = 'Growth';

  // Two tabs on one page: the keys, and the integration guide at `.../guide`.
  const location = useLocation();
  const currentPath = location.pathname.replace(/\/+$/, '');
  const onGuideTab = currentPath.endsWith('/guide');
  const keysPath = onGuideTab ? currentPath.slice(0, -'/guide'.length) : currentPath;
  const guidePath = `${keysPath}/guide`;

  const [loading, setLoading] = useState(true);
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newKey, setNewKey] = useState<ApiKeyWithSecret | null>(null);
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  // Per-day series plus the by-endpoint / by-error breakdowns, per key.
  const [keyUsage, setKeyUsage] = useState<Record<string, ApiKeyUsageSummary>>({});
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [rotatingId, setRotatingId] = useState<string | null>(null);
  const [editingProductKey, setEditingProductKey] = useState<ApiKey | null>(null);
  const [editingIpsKey, setEditingIpsKey] = useState<ApiKey | null>(null);
  const productSuggestions = apiKeysService.getProductSuggestions(apiKeys);

  useEffect(() => {
    loadApiKeys();
  }, []);

  const loadApiKeys = async () => {
    setLoading(true);
    try {
      const keys = await apiKeysService.getApiKeys();
      setApiKeys(keys);
    } catch (error) {
      toast.error('Failed to load API keys');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyCreated = (key: ApiKeyWithSecret) => {
    setShowCreateModal(false);
    setNewKey(key);
    loadApiKeys();
  };

  const handleToggleActive = async (key: ApiKey) => {
    try {
      await apiKeysService.updateApiKey(key.id, { isActive: !key.isActive });
      toast.success(`API key ${key.isActive ? 'disabled' : 'enabled'}`);
      loadApiKeys();
    } catch (error) {
      toast.error('Failed to update API key');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this API key? This action cannot be undone.')) {
      return;
    }
    setDeletingId(id);
    try {
      await apiKeysService.deleteApiKey(id);
      toast.success('API key deleted');
      setApiKeys(apiKeys.filter((k) => k.id !== id));
    } catch (error) {
      toast.error('Failed to delete API key');
    } finally {
      setDeletingId(null);
    }
  };

  const handleRotate = async (id: string) => {
    if (!confirm('Are you sure you want to rotate this API key? The old key will stop working immediately.')) {
      return;
    }
    setRotatingId(id);
    try {
      const rotatedKey = await apiKeysService.rotateApiKey(id);
      setNewKey(rotatedKey);
      toast.success('API key rotated');
      loadApiKeys();
    } catch (error) {
      toast.error('Failed to rotate API key');
    } finally {
      setRotatingId(null);
    }
  };

  const handleExpandKey = async (keyId: string) => {
    if (expandedKey === keyId) {
      setExpandedKey(null);
      return;
    }
    setExpandedKey(keyId);
    // Always re-read on expand: an admin troubleshooting an integration
    // collapses and re-opens the card to see whether a retry got through.
    // The previous numbers stay on screen until the new ones arrive.
    await loadKeyUsage(keyId);
  };

  const loadKeyUsage = async (keyId: string) => {
    try {
      const usage = await apiKeysService.getApiKeyUsageSummary(keyId);
      setKeyUsage((prev) => ({ ...prev, [keyId]: usage }));
    } catch (error) {
      // Keep whatever is already shown; with nothing to show, say usage is
      // unavailable rather than leaving the spinner up forever.
      setKeyUsage((prev) =>
        prev[keyId]
          ? prev
          : {
              ...prev,
              [keyId]: { daily: [], byEndpoint: [], byError: [], unavailable: true },
            },
      );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">API Keys</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Manage API keys for external integrations
          </p>
        </div>
        <Button
          onClick={() => setShowCreateModal(true)}
          disabled={!apiKeysEntitled}
          title={
            !apiKeysEntitled
              ? `API keys require the ${requiredPlanForApiKeys} plan or higher`
              : undefined
          }
        >
          <Plus className="h-4 w-4 mr-2" />
          Create API Key
        </Button>
      </div>

      <div role="tablist" aria-label="API keys" className="flex gap-6 border-b border-gray-200 dark:border-gray-700">
        {[
          { to: keysPath, label: 'Keys', icon: Key, selected: !onGuideTab },
          { to: guidePath, label: 'Integration guide', icon: BookOpen, selected: onGuideTab },
        ].map((tab) => (
          <Link
            key={tab.label}
            to={tab.to}
            role="tab"
            aria-selected={tab.selected}
            className={`inline-flex items-center gap-2 pb-3 -mb-px border-b-2 text-sm font-medium ${
              tab.selected
                ? 'border-primary-600 text-primary-700 dark:text-primary-300'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
            }`}
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
          </Link>
        ))}
      </div>

      {onGuideTab ? (
        <IntegrationGuide
          apiKeys={apiKeys}
          apiKeysEntitled={apiKeysEntitled}
          requiredPlan={requiredPlanForApiKeys}
        />
      ) : (
      <>
      {/* P0.2 — plan-tier upgrade prompt. Shown to Free tenants in place
          of the security-best-practices card. Clicking through goes to
          the existing /settings/billing route. */}
      {!apiKeysEntitled ? (
        <Card className="p-4 bg-amber-50 dark:bg-amber-900/20 border-amber-200">
          <div className="flex gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800 dark:text-amber-200 flex-1">
              <p className="font-medium">API keys require an upgrade</p>
              <p className="mt-1">
                API access is included in the {requiredPlanForApiKeys} plan
                and higher. Upgrade your subscription to issue keys for
                external integrations.
              </p>
              <Link
                to={
                  user?.tenantId
                    ? `/settings/tenant/${user.tenantId}/general`
                    : '/settings'
                }
                className="inline-flex items-center mt-3 px-3 py-1.5 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-md"
              >
                View plans
              </Link>
            </div>
          </div>
        </Card>
      ) : (
        <Card className="p-4 bg-blue-50 dark:bg-blue-900/20 border-blue-200">
          <div className="flex gap-3">
            <Shield className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-700 dark:text-blue-300">
              <p className="font-medium">API Key Security Best Practices</p>
              <ul className="mt-1 list-disc list-inside space-y-1">
                <li>Never expose API keys in client-side code or public repositories</li>
                <li>Use the minimum required permissions for each key</li>
                <li>Rotate keys regularly and revoke unused ones</li>
                <li>Monitor usage for unexpected patterns</li>
              </ul>
            </div>
          </div>
        </Card>
      )}

      {/* API Keys List */}
      <div className="space-y-4">
        {apiKeys.length === 0 ? (
          <Card className="p-8 text-center">
            <Key className="h-12 w-12 text-gray-400 mx-auto mb-4 dark:text-gray-500" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No API Keys</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              {apiKeysEntitled
                ? 'Create an API key to start integrating with external services'
                : `API keys require the ${requiredPlanForApiKeys} plan. Upgrade to start integrating with external services.`}
            </p>
            <Button
              onClick={() => setShowCreateModal(true)}
              disabled={!apiKeysEntitled}
            >
              <Plus className="h-4 w-4 mr-2" />
              {apiKeysEntitled
                ? 'Create Your First API Key'
                : 'Upgrade to Create Keys'}
            </Button>
            <p className="mt-4 text-sm">
              <Link to={guidePath} className="text-primary-600 dark:text-primary-400 hover:underline">
                Read the integration guide
              </Link>
            </p>
          </Card>
        ) : (
          apiKeys.map((key) => {
            const expiryStatus = apiKeysService.getExpiryStatus(key.expiresAt);
            const isExpanded = expandedKey === key.id;

            return (
              <Card key={key.id} className="overflow-hidden">
                <div className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div
                        className={`h-10 w-10 rounded-lg flex items-center justify-center ${
                          key.isActive && !apiKeysService.isExpired(key.expiresAt)
                            ? 'bg-green-100'
                            : 'bg-gray-100 dark:bg-gray-700'
                        }`}
                      >
                        <Key
                          className={`h-5 w-5 ${
                            key.isActive && !apiKeysService.isExpired(key.expiresAt)
                              ? 'text-green-600'
                              : 'text-gray-400 dark:text-gray-500'
                          }`}
                        />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium text-gray-900 dark:text-white">{key.name}</h3>
                          {key.isActive && !apiKeysService.isExpired(key.expiresAt) ? (
                            <Badge variant="success">Active</Badge>
                          ) : apiKeysService.isExpired(key.expiresAt) ? (
                            <Badge variant="error">Expired</Badge>
                          ) : (
                            <Badge variant="default">Disabled</Badge>
                          )}
                        </div>
                        {key.description && (
                          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{key.description}</p>
                        )}
                        <div className="flex items-center gap-1.5 mt-1 text-xs">
                          <Package className="h-3 w-3 text-gray-400 dark:text-gray-500" aria-hidden />
                          {key.productName ? (
                            <span
                              className="inline-flex items-center px-2 py-0.5 rounded-md font-medium bg-violet-50 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300"
                              title={key.productSlug ? `Ticket source: ${key.productSlug}` : undefined}
                            >
                              {key.productName}
                            </span>
                          ) : (
                            <span className="text-gray-400 dark:text-gray-500">No product · tickets show as API</span>
                          )}
                          <button
                            type="button"
                            onClick={() => setEditingProductKey(key)}
                            className="p-0.5 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-200"
                            title="Edit product"
                            aria-label={`Edit product for ${key.name}`}
                          >
                            <Pencil className="h-3 w-3" />
                          </button>
                        </div>
                        <div className="flex items-center gap-4 mt-2 text-xs text-gray-500 dark:text-gray-400">
                          <span className="font-mono bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded">
                            {apiKeysService.formatKeyPrefix(key.keyPrefix)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {apiKeysService.getLastUsedLabel(key.lastUsedAt)}
                          </span>
                          {/* Derive the request count from the same 7-day series
                              the chart uses, so the collapsed-view counter and the
                              expanded chart can never disagree. Hidden until the
                              user expands the card (usage loads lazily); previously
                              this read `key.usageCount` — a separate lifetime
                              field that wasn't being updated end-to-end and
                              displayed a misleading "0 requests" even when the
                              chart showed real traffic. */}
                          {keyUsage[key.id] && (
                            <span className="flex items-center gap-1">
                              <Activity className="h-3 w-3" />
                              {apiKeysService.formatUsageCount(
                                keyUsage[key.id].daily.reduce(
                                  (sum, d) => sum + d.requests,
                                  0,
                                ),
                              )}{' '}
                              <span className="opacity-70">last 7d</span>
                            </span>
                          )}
                          <span className={expiryStatus.color}>{expiryStatus.label}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleToggleActive(key)}
                        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
                          key.isActive ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-600'
                        }`}
                      >
                        <span
                          className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
                            key.isActive ? 'translate-x-5' : 'translate-x-0'
                          }`}
                        />
                      </button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRotate(key.id)}
                        disabled={rotatingId === key.id}
                        title="Rotate key"
                      >
                        {rotatingId === key.id ? (
                          <Spinner size="sm" />
                        ) : (
                          <RotateCcw className="h-4 w-4" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(key.id)}
                        disabled={deletingId === key.id}
                        className="text-red-600 hover:text-red-700"
                        title="Delete key"
                      >
                        {deletingId === key.id ? (
                          <Spinner size="sm" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                      </Button>
                      <button
                        onClick={() => handleExpandKey(key.id)}
                        className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 dark:text-gray-500"
                      >
                        {isExpanded ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Expanded Details — stacked layout:
                      1. Stats strip derived from the same 7-day series as the
                         chart (so numbers can't diverge from the bars)
                      2. Permissions as inline chips
                      3. Full-width usage chart
                      4. Compact inline metadata footer
                      Replaces a previous 2-col grid (Permissions | Chart) that
                      left ~70% of the left column blank whenever a key had only
                      one permission, plus a separate 3-col metadata row. */}
                  {isExpanded && (
                    <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 space-y-4">
                      {/* Stats strip — only when usage has loaded; while it's
                          loading the spinner below carries the indeterminate
                          state. Computing here (not memoizing) is cheap — the
                          series is at most 7 entries. */}
                      {keyUsage[key.id] && (() => {
                        const series = keyUsage[key.id].daily;
                        const total = series.reduce((s, d) => s + d.requests, 0);
                        const errors = series.reduce((s, d) => s + d.errors, 0);
                        const errorRate = total > 0 ? (errors / total) * 100 : 0;
                        const avgPerDay = series.length > 0 ? total / series.length : 0;
                        return (
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                            <Stat
                              label="Requests · 7d"
                              value={apiKeysService.formatUsageCount(total)}
                            />
                            <Stat
                              label="Errors · 7d"
                              value={apiKeysService.formatUsageCount(errors)}
                              accent={errors > 0 ? 'red' : undefined}
                            />
                            <Stat
                              label="Error rate"
                              value={`${errorRate.toFixed(1)}%`}
                              accent={errorRate > 10 ? 'red' : undefined}
                            />
                            <Stat
                              label="Avg / day"
                              value={avgPerDay.toFixed(avgPerDay >= 10 ? 0 : 1)}
                            />
                          </div>
                        );
                      })()}

                      {/* Permissions — inline chips, primary color so they read
                          as the meaningful capability scope, not chrome. */}
                      <div>
                        <h4 className="text-[11px] font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1.5">
                          Permissions
                        </h4>
                        <div className="flex flex-wrap gap-1.5">
                          {key.permissions.map((permission) => (
                            <span
                              key={permission}
                              className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300"
                            >
                              {apiKeysService.getPermissionLabel(permission)}
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* Usage Chart — full width, taller is unnecessary because
                          the stats strip carries the headline number; the bars
                          are for trend, not for precise readout. */}
                      <div>
                        <h4 className="text-[11px] font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1.5">
                          Usage (Last 7 Days)
                        </h4>
                        {keyUsage[key.id] ? (
                          <UsageChart usage={keyUsage[key.id].daily} />
                        ) : (
                          <div className="flex items-center justify-center h-32">
                            <Spinner size="sm" />
                          </div>
                        )}
                      </div>

                      {/* Which calls fail, and why: by endpoint, by error type
                          (same 7-day window as the tiles), then the individual
                          requests. The log loads itself; its Refresh also
                          re-reads the totals so the two never drift apart. */}
                      {keyUsage[key.id] && (
                        <ApiKeyUsageBreakdown summary={keyUsage[key.id]} days={7} />
                      )}
                      {/* Admins only: the log shows caller IPs and error
                          details. The BFF and tenant-service enforce it; this
                          just avoids a request that would be refused. */}
                      {apiKeysService.canViewRequestLog(user?.role) ? (
                        <ApiKeyRequestLog
                          apiKeyId={key.id}
                          onRefresh={() => loadKeyUsage(key.id)}
                        />
                      ) : (
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Recent requests are visible to admins only.
                        </p>
                      )}

                      {/* Compact metadata footer — single inline row instead of
                          a 3-col grid. Dot separators reduce visual noise; the
                          row reflows to two lines on narrow viewports without
                          extra markup. */}
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 pt-3 border-t border-gray-100 dark:border-gray-800 text-xs">
                        <span>
                          <span className="text-gray-500 dark:text-gray-400">Rate limit</span>
                          <span className="ml-1.5 font-medium text-gray-900 dark:text-white tabular-nums">
                            {key.rateLimit || 1000}/min
                          </span>
                        </span>
                        <span className="text-gray-300 dark:text-gray-600" aria-hidden>·</span>
                        <span className="inline-flex items-center">
                          <span className="text-gray-500 dark:text-gray-400">Allowed IPs</span>
                          <span
                            className="ml-1.5 text-gray-900 dark:text-white"
                            title={(key.ipWhitelist ?? []).join(', ') || undefined}
                          >
                            {(key.ipWhitelist ?? []).length === 0
                              ? 'Any'
                              : (key.ipWhitelist ?? []).length <= 2
                                ? (key.ipWhitelist ?? []).join(', ')
                                : `${(key.ipWhitelist ?? []).length} ranges`}
                          </span>
                          <button
                            type="button"
                            onClick={() => setEditingIpsKey(key)}
                            className="ml-1 p-0.5 text-gray-400 hover:text-primary-600 dark:text-gray-500"
                            title="Edit allowed IP addresses"
                            aria-label={`Edit allowed IP addresses for ${key.name}`}
                          >
                            <Pencil className="h-3 w-3" />
                          </button>
                        </span>
                        <span className="text-gray-300 dark:text-gray-600" aria-hidden>·</span>
                        <span>
                          <span className="text-gray-500 dark:text-gray-400">Created</span>
                          <span className="ml-1.5 text-gray-900 dark:text-white tabular-nums">
                            {new Date(key.createdAt).toLocaleDateString()}
                          </span>
                        </span>
                        <span className="text-gray-300 dark:text-gray-600" aria-hidden>·</span>
                        <span>
                          <span className="text-gray-500 dark:text-gray-400">By</span>
                          <span className="ml-1.5 text-gray-900 dark:text-white">
                            {apiKeysService.getCreatedByLabel(key)}
                          </span>
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </Card>
            );
          })
        )}
      </div>
      </>
      )}

      {/* Modals */}
      <CreateKeyModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreated={handleKeyCreated}
        productSuggestions={productSuggestions}
      />

      <EditProductModal
        apiKey={editingProductKey}
        suggestions={productSuggestions}
        onClose={() => setEditingProductKey(null)}
        onSaved={() => {
          setEditingProductKey(null);
          loadApiKeys();
        }}
      />

      <EditIpAllowListModal
        apiKey={editingIpsKey}
        onClose={() => setEditingIpsKey(null)}
        onSaved={() => {
          setEditingIpsKey(null);
          loadApiKeys();
        }}
      />

      <NewKeyModal
        isOpen={!!newKey}
        onClose={() => setNewKey(null)}
        apiKey={newKey}
        guidePath={guidePath}
      />
    </div>
  );
}
